// ============================================================
// Die Vorschau (SPEC §52) — wo sich Abgaben stauen, je Büro und Woche.
//
// Zwei Leserouten, nichts zum Schreiben: `GET /vorschau` liefert das Raster
// (Büros × die nächsten zwölf ISO-Wochen, je Zelle Zahlen und Dichte),
// `GET /vorschau/zelle` die Einträge EINER Zelle, sobald jemand sie anklickt.
// Beides nur aus freigegebenen Projekten und nur als Projekt- und Büro-Aggregat —
// keine Werte je Person, keine Abwesenheiten (E52.1, BetrVG §87).
//
// Eine eigene Datei neben `hooks.ts` wie `cockpit.ts` und `freigaben.ts`: `hooks.ts`
// ist die meistgeänderte Datei des Frontends, und dieser Vertrag entsteht neben dem
// §39-Bau, der sie gerade anfasst (§52.13). Die Regeln aus ihrem Kopf gelten hier
// genauso — Serverdaten gehören TanStack Query, Pfade ohne `/api`.
//
// ---- Vier Festlegungen ----------------------------------------------------------
//
// 1. DIE SCHLÜSSEL LIEGEN UNTER `["vorschau", …]`, nicht unter `["projekt", id]`
//    oder `["abgaben"]`. Eine Zelle rechnet aus vielen Projekten, und die Meldungen
//    aus dem Ereignisstrom (`lib/ereignisse.ts`, §52.10) treffen mit EINEM Präfix
//    Raster und jede geöffnete Zelle zugleich.
//
// 2. DIE STARTSEITE FRAGT DIESELBE ANTWORT (E52.3): ihre Zeile „Diese und nächste
//    Woche im Büro" liest die Zellen dieser und der nächsten Woche (über `zelle.woche`,
//    nicht über den Index — §52.11 `zweiWochen`) aus `useVorschau()` mit der Vorgabe
//    von zwölf Wochen — EIN Eintrag im Zwischenspeicher für beide Stellen, und wer
//    von der Startseite in die Vorschau springt, wartet nicht.
//
// 3. DIE ZELLE LÄDT ERST BEIM ANKLICKEN (`useVorschauZelle` ist ohne Büro oder
//    Woche aus). Das Raster selbst bringt nur Zahlen — zwölf Wochen mal vier Büros
//    Einträge vorab zu laden hieße, fast immer Ungelesenes zu holen.
//
// 4. DIE LAUFENDE WOCHE STECKT IM SCHLÜSSEL (`raster(wochen, montag)`, §52.10). Die
//    Antwort beginnt mit der Woche, in der der Server gefragt wurde. Hinge „diese
//    Woche" nur an `wochen[0]` einer Antwort unter einem datumslosen Schlüssel, bliebe
//    sie über Mitternacht von Sonntag auf Montag stehen — `staleTime` allein löst
//    keinen Abruf aus —, und die Startzeile zählte ab Montag 00:00 Vorwoche und diese
//    Woche. Mit dem Montag im Schlüssel fragt das erste Zeichnen der neuen Woche neu.
//
// Hausregel (§38.11): `null` aus dem Netz kann als `undefined` ankommen — `beginn`,
// `phase_id`, `termin_art`, `abgabe_status`, `frist_art` immer mit `== null` prüfen.
// ============================================================
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { heuteIso } from "../lib/format";
import { api, frage } from "./client";
import type { Vorschau, VorschauEintrag } from "./types";

/* ========================================================== Zahlen */

/** Wie viele Wochen die Ansicht zeigt (E52.1) — dieselbe Vorgabe wie `VORSCHAU_WOCHEN` im Router. */
export const VORSCHAU_WOCHEN = 12;
/** Mehr nimmt der Server nicht (`?wochen=` 1 bis 26, sonst 422). */
export const VORSCHAU_WOCHEN_MAX = 26;
/** Wie viele Wochen die Zeile der Startseite zusammenzählt: diese und nächste (E52.3) — die
 *  Zellen mit `woche` = `montagIso()` + 7·k für k < START_WOCHEN, nicht die ersten nach Index. */
export const START_WOCHEN = 2;

/** Wie lange eine Antwort als frisch gilt. Der Ereignisstrom entwertet ohnehin (§52.10);
 *  das hier verhindert nur, dass jeder Wechsel Galerie → Vorschau neu fragt. */
const FRISCH_MS = 60_000;

const MS_TAG = 86_400_000;

/* ========================================================== Die laufende Woche */

/**
 * Der Montag der laufenden ISO-Woche in Europe/Berlin, "YYYY-MM-DD" (§52.10) — derselbe Wert
 * wie `woche().montag` aus `screens/start/hilfen.ts`, geprüft in `vorschau.test.ts`. Eigene
 * Zeilen statt eines Imports, weil `api/` nicht aus `screens/` liest (sonst zöge jeder, der die
 * Vorschau fragt, die Hilfen der Startseite mit); die Regel ist dieselbe: der Tag kommt über
 * `heuteIso` (Berlin, nicht die Uhr des Geräts), und der Sonntag zählt als 7. Tag — sonst
 * gehörte er zur FOLGENDEN Woche.
 */
export function montagIso(jetzt: Date = new Date()): string {
  const heute = heuteIso(jetzt);
  const t = Date.UTC(Number(heute.slice(0, 4)), Number(heute.slice(5, 7)) - 1, Number(heute.slice(8, 10)));
  const wochentag = new Date(t).getUTCDay() || 7;
  return new Date(t - (wochentag - 1) * MS_TAG).toISOString().slice(0, 10);
}

/* ========================================================== Schlüssel */

/** Alle unter dem Präfix `["vorschau"]` — ein `invalidateQueries` darauf trifft Raster und Zellen. */
export const vorschauSchluessel = {
  /** Jede Vorschau-Abfrage — für `lib/ereignisse.ts` (`VORSCHAU_TABELLEN`, §52.10). */
  alle: ["vorschau"] as const,
  /** Das Raster für `wochen` Wochen ab der Woche, deren Montag `montag` ist (`montagIso()`) —
   *  der Montag macht aus jeder neuen Woche einen neuen Schlüssel (Festlegung 4). */
  raster: (wochen: number, montag: string) => ["vorschau", "raster", wochen, montag] as const,
  /** Die Einträge EINER Zelle: Büro-Kennung und Montag der Woche ("YYYY-MM-DD"). */
  zelle: (buero: string, woche: string) => ["vorschau", "zelle", buero, woche] as const,
};

/* ========================================================== Lesen */

/**
 * `GET /vorschau?wochen=` → `Vorschau`: je Büro (alle, auch leere, in `sortierung`)
 * eine Zelle je Woche, ab der laufenden Berliner ISO-Woche (§52.2). Die Dichte
 * (`stufe`) rechnet der Server, gemessen am Schnitt des Büros über zwölf Wochen —
 * unabhängig von `wochen` (§52.5). `aktiv` für die Startseite: ohne Heimatbüro
 * fragt sie nicht (§52.3). Der Schlüssel trägt den Berliner Montag, bei jedem
 * Zeichnen neu gerechnet (Festlegung 4).
 */
export function useVorschau(wochen: number = VORSCHAU_WOCHEN, aktiv = true): UseQueryResult<Vorschau> {
  const montag = montagIso();
  return useQuery({
    queryKey: vorschauSchluessel.raster(wochen, montag),
    queryFn: () => api.get<Vorschau>("/vorschau" + frage({ wochen })),
    enabled: aktiv,
    staleTime: FRISCH_MS,
  });
}

/**
 * `GET /vorschau/zelle?buero=&woche=` → `VorschauEintrag[]` einer Zelle, nach Tag,
 * Abgabe vor Termin, Uhrzeit, Projekt, Titel (§52.2). Aus, solange `buero` oder
 * `woche` fehlt — die Ansicht reicht hier die gewählte Zelle (aus `?buero=&woche=`
 * der Adresse) durch. `woche` ist ein MONTAG; jeder andere Tag ist 422.
 */
export function useVorschauZelle(
  buero: string | null | undefined,
  woche: string | null | undefined,
): UseQueryResult<VorschauEintrag[]> {
  return useQuery({
    queryKey: vorschauSchluessel.zelle(buero ?? "", woche ?? ""),
    queryFn: () => api.get<VorschauEintrag[]>("/vorschau/zelle" + frage({ buero, woche })),
    enabled: !!buero && !!woche,
    staleTime: FRISCH_MS,
  });
}
